import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Zap, Users, Crown, Mail, RefreshCw, ShieldAlert, Rocket } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CURRENCY_SYMBOL = { NZD: "$", USD: "$", AUD: "$", GBP: "£", EUR: "€" };

export const Admin = () => {
  const [params] = useSearchParams();
  const key = params.get("key") || "";
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(false);
  const [blasting, setBlasting] = useState(false);
  const [blastAt, setBlastAt] = useState("");
  const [scheduling, setScheduling] = useState(false);

  const schedule = async () => {
    if (!blastAt) { toast.error("Pick a date and time first"); return; }
    setScheduling(true);
    try {
      const res = await axios.post(`${API}/admin/schedule-blast`, null, {
        params: { key, run_at: new Date(blastAt).toISOString() },
      });
      toast.success(`Blast scheduled for ${new Date(res.data.scheduled_for).toLocaleString()}`);
      load();
    } catch {
      toast.error("Couldn't schedule — check the date");
    } finally {
      setScheduling(false);
    }
  };

  const digestNow = async () => {
    try {
      const res = await axios.post(`${API}/admin/send-digest`, null, { params: { key } });
      toast.success(`Digest sent — ${res.data.new_members} new members, ${res.data.new_waitlist} waitlist signups this week`);
    } catch {
      toast.error("Digest failed — check the backend logs");
    }
  };

  const cancelBlast = async () => {
    if (!window.confirm("Cancel the scheduled launch blast?")) return;
    try {
      await axios.post(`${API}/admin/cancel-blast`, null, { params: { key } });
      toast.success("Scheduled blast cancelled");
      setBlastAt("");
      load();
    } catch {
      toast.error("Couldn't cancel — try again");
    }
  };

  const blast = async () => {
    if (!window.confirm("Email EVERY waitlist signup and founding member that fit50 is live?")) return;
    setBlasting(true);
    try {
      const res = await axios.post(`${API}/admin/launch-blast`, null, { params: { key } });
      toast.success(`Launch email sent to ${res.data.sent} people (${res.data.failed} failed)`);
    } catch {
      toast.error("Blast failed — check the backend logs");
    } finally {
      setBlasting(false);
    }
  };

  const load = () => {
    setError(false);
    axios
      .get(`${API}/admin/stats`, { params: { key } })
      .then((res) => setStats(res.data))
      .catch(() => { setStats(null); setError(true); });
  };

  useEffect(load, [key]);

  if (error) {
    return (
      <div className="min-h-screen bg-obsidian grid place-items-center px-6">
        <div data-testid="admin-unauthorized" className="text-center">
          <ShieldAlert className="h-10 w-10 text-volt mx-auto" />
          <h1 className="font-display font-black uppercase text-3xl mt-6">Unauthorized</h1>
          <p className="mt-3 text-sm text-neutral-500">A valid admin key is required to view this page.</p>
        </div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="min-h-screen bg-obsidian grid place-items-center">
        <p className="font-mono text-xs tracking-widest uppercase text-neutral-500 animate-pulse">Loading…</p>
      </div>
    );
  }

  const soldPct = Math.min((stats.founding_spots_sold / stats.founding_spots_total) * 100, 100);

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-obsidian relative">
      <div className="absolute inset-0 grid-lines" />
      <div className="relative max-w-5xl mx-auto px-4 sm:px-6 py-14">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="grid place-items-center h-8 w-8 bg-volt text-white">
              <Zap className="h-4 w-4" strokeWidth={3} />
            </span>
            <span className="font-display font-black text-2xl uppercase">Fit<span className="text-volt">/</span>50 <span className="text-neutral-500 text-lg">Sales</span></span>
          </div>
          <button
            data-testid="admin-refresh-button"
            onClick={load}
            className="inline-flex items-center gap-2 border border-white/20 px-4 py-2 font-mono text-[11px] tracking-widest uppercase text-neutral-300 hover:border-volt hover:text-volt transition-colors"
          >
            <RefreshCw className="h-3.5 w-3.5" />
            Refresh
          </button>
          <button
            data-testid="admin-digest-button"
            onClick={digestNow}
            className="inline-flex items-center gap-2 border border-white/20 px-4 py-2 font-mono text-[11px] tracking-widest uppercase text-neutral-300 hover:border-volt hover:text-volt transition-colors"
          >
            <Mail className="h-3.5 w-3.5" />
            Digest Now
          </button>
          <button
            data-testid="admin-launch-blast-button"
            onClick={blast}
            disabled={blasting}
            className="inline-flex items-center gap-2 bg-volt text-white px-4 py-2 font-mono text-[11px] tracking-widest uppercase font-semibold hover:bg-volt-hover disabled:opacity-60 transition-colors"
          >
            <Rocket className="h-3.5 w-3.5" />
            {blasting ? "Sending..." : "Launch-Day Blast"}
          </button>
        </div>

        <div className="mt-10 grid sm:grid-cols-2 gap-4">
          <div className="border border-volt/50 bg-carbon p-7">
            <div className="flex items-center gap-2 font-mono text-[10px] tracking-widest uppercase text-neutral-500">
              <Crown className="h-4 w-4 text-volt" /> Founding Spots Sold
            </div>
            <p data-testid="admin-spots-sold" className="font-display font-black text-6xl mt-4 text-volt">
              {stats.founding_spots_sold}<span className="text-2xl text-neutral-500"> / {stats.founding_spots_total}</span>
            </p>
            <div className="mt-4 h-1.5 bg-white/10 overflow-hidden">
              <div className="h-full bg-volt transition-all duration-700" style={{ width: `${soldPct}%` }} />
            </div>
          </div>

          <div className="border border-white/10 bg-carbon p-7">
            <div className="flex items-center gap-2 font-mono text-[10px] tracking-widest uppercase text-neutral-500">
              <Mail className="h-4 w-4 text-volt" /> Waitlist Signups
            </div>
            <p data-testid="admin-waitlist-count" className="font-display font-black text-6xl mt-4">{stats.waitlist_count}</p>
            <p className="mt-4 font-mono text-[10px] tracking-widest uppercase text-neutral-500">Emails captured, ready for launch</p>
          </div>
        </div>

        <div className="mt-4 border border-white/10 bg-carbon p-7">
          <div className="flex items-center gap-2 font-mono text-[10px] tracking-widest uppercase text-neutral-500">
            <Users className="h-4 w-4 text-volt" /> Revenue By Currency
          </div>
          {Object.keys(stats.revenue_by_currency).length === 0 ? (
            <p className="mt-5 text-sm text-neutral-500">No paid orders yet — revenue appears here the moment the first payment lands.</p>
          ) : (
            <div className="mt-5 grid grid-cols-2 sm:grid-cols-5 gap-3">
              {Object.entries(stats.revenue_by_currency).map(([cur, amt]) => (
                <div key={cur} data-testid={`admin-revenue-${cur.toLowerCase()}`} className="border border-white/10 bg-obsidian p-4 text-center">
                  <p className="font-display font-black text-2xl text-volt">{CURRENCY_SYMBOL[cur]}{amt}</p>
                  <p className="font-mono text-[9px] tracking-widest uppercase text-neutral-500 mt-1">{cur}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        <div data-testid="admin-blast-scheduler" className="mt-4 border border-white/10 bg-carbon p-7">
          <p className="font-mono text-[10px] tracking-widest uppercase text-neutral-500">Schedule The Launch Blast</p>
          <div className="mt-4 flex flex-col sm:flex-row gap-3">
            <input
              data-testid="admin-blast-date-input"
              type="datetime-local"
              value={blastAt}
              onChange={(e) => setBlastAt(e.target.value)}
              className="bg-obsidian border border-white/15 px-4 py-3 text-sm text-neutral-100 focus:outline-none focus:border-volt transition-colors [color-scheme:dark]"
            />
            <button
              data-testid="admin-schedule-blast-button"
              onClick={schedule}
              disabled={scheduling}
              className="inline-flex items-center justify-center gap-2 bg-volt text-white px-6 py-3 font-mono text-[11px] tracking-widest uppercase font-semibold hover:bg-volt-hover disabled:opacity-60 transition-colors"
            >
              {scheduling ? "Scheduling..." : "Schedule Blast"}
            </button>
          </div>
          <p data-testid="admin-blast-status" className="mt-4 font-mono text-[11px] tracking-widest uppercase">
            {stats.blast_schedule?.sent_at ? (
              <span className="text-volt">Blast sent {new Date(stats.blast_schedule.sent_at).toLocaleString()} — {stats.blast_schedule.sent} delivered, {stats.blast_schedule.failed} failed</span>
            ) : stats.blast_schedule?.run_at ? (
              <span className="text-volt">Scheduled for {new Date(stats.blast_schedule.run_at).toLocaleString()}</span>
            ) : (
              <span className="text-neutral-500">Not scheduled — pick your app-store launch moment above</span>
            )}
          </p>
          {stats.blast_schedule?.run_at && !stats.blast_schedule?.sent_at && (
            <button
              data-testid="admin-cancel-blast-button"
              onClick={cancelBlast}
              className="mt-3 inline-flex items-center gap-2 border border-white/25 px-4 py-2 font-mono text-[11px] tracking-widest uppercase text-neutral-300 hover:border-destructive hover:text-destructive transition-colors"
            >
              Cancel Scheduled Blast
            </button>
          )}
        </div>

        <div className="mt-4 border border-white/10 bg-carbon p-7">
          <p className="font-mono text-[10px] tracking-widest uppercase text-neutral-500">Recent Paid Orders</p>
          {stats.recent.length === 0 ? (
            <p className="mt-4 text-sm text-neutral-500">Nothing yet.</p>
          ) : (
            <div className="mt-4 space-y-2">
              {stats.recent.map((t) => (
                <div key={t.session_id} className="flex flex-wrap items-center justify-between gap-2 border-b border-white/5 pb-2">
                  <span className="font-mono text-[11px] uppercase tracking-wider text-volt">{t.plan}</span>
                  <span className="font-mono text-[11px] uppercase tracking-wider text-neutral-400">{(t.currency || "nzd").toUpperCase()}</span>
                  <span className="font-mono text-[10px] text-neutral-600">{(t.updated_at || t.created_at || "").slice(0, 16).replace("T", " ")}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <Link to="/" className="mt-8 inline-block font-mono text-[11px] tracking-widest uppercase text-neutral-500 hover:text-volt transition-colors">
          ← Back to launch page
        </Link>
        <Link to={`/checklist?key=${key}`} className="mt-8 ml-6 inline-block font-mono text-[11px] tracking-widest uppercase text-neutral-500 hover:text-volt transition-colors">
          Pre-flight checklist →
        </Link>
      </div>
    </div>
  );
};
